import React, { useState } from 'react';
import { Firestore } from 'firebase/firestore';
import { Wand2, Plus, Loader, ChevronDown, Image, Check, X } from 'lucide-react';
import { PatternConfig } from 'services/apiService';
import { useProjectsWithThreadsRealtime } from 'hooks/useMyThreadsRealtime';
import { useImagesFromFirestore } from 'hooks/useMediaFromFirestore';

// Thread Selector Modal Component
const ThreadSelectorModal: React.FC<{
  threads: Thread[];
  onSelect: (thread: Thread) => void;
  onClose: () => void;
  selectedThreadId?: string;
}> = ({ threads, onSelect, onClose, selectedThreadId }) => {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-800 border border-gray-700 rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Select a Thread</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {threads.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No threads available</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {threads.map(thread => (
                <button
                  key={thread.thread_id}
                  onClick={() => onSelect(thread)}
                  className={`relative bg-gray-900/50 border rounded-lg p-3 text-left hover:border-blue-500/50 transition-all ${
                    selectedThreadId === thread.thread_id
                      ? 'border-blue-500 ring-2 ring-blue-500/30'
                      : 'border-gray-700'
                  }`}
                >
                  {selectedThreadId === thread.thread_id && (
                    <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-0.5">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div className="aspect-square bg-gray-950 rounded-lg overflow-hidden mb-2">
                    {thread.thumbnail_url ? (
                      <img
                        src={thread.thumbnail_url}
                        alt={thread.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/placeholder-thumb.png'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-8 h-8 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-200 truncate">{thread.name}</p>
                  <p className="text-xs text-gray-500">{thread.length} images</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Picture Selector Modal Component
const PictureSelectorModal: React.FC<{
  images: IImage[];
  loading: boolean;
  onSelect: (image: IImage) => void;
  onClose: () => void;
  selectedPictureId?: string;
  title: string;
}> = ({ images, loading, onSelect, onClose, selectedPictureId, title }) => {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-800 border border-gray-700 rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : images.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No images in this thread</p>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {images.map((image, idx) => (
                <button
                  key={image.picture_id}
                  onClick={() => onSelect(image)}
                  className={`relative bg-gray-900/50 border rounded-lg overflow-hidden hover:border-blue-500/50 transition-all ${
                    selectedPictureId === image.picture_id
                      ? 'border-blue-500 ring-2 ring-blue-500/30'
                      : 'border-gray-700'
                  }`}
                >
                  {selectedPictureId === image.picture_id && (
                    <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-0.5 z-10">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div className="aspect-square bg-gray-950">
                    <img
                      src={image.output_url || image.thumb_url}
                      alt={`${idx + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-2">
                    <p className="text-xs text-gray-400">Step {image.seq}</p>
                    <p className="text-xs text-gray-500 truncate">{image.op}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Pattern Source Selector - Thread/Picture selection for pattern source
interface PromptOverrideEditorProps {
  projectId: string;
  db: Firestore;
  uid: string;
  patternConfig: PatternConfig;
  onPatternConfigChange: (updates: Partial<PatternConfig>) => void;
  onLoadPattern: () => void;
  isLoadingPattern: boolean;
  patternError?: string;
}

const PromptOverrideEditor: React.FC<PromptOverrideEditorProps> = ({
  projectId, db, uid, patternConfig, onPatternConfigChange, onLoadPattern, isLoadingPattern, patternError
}) => {
  const { projects, loading: projectsLoading } = useProjectsWithThreadsRealtime(db, uid);
  const [showThreadModal, setShowThreadModal] = useState(false);
  const [showFromPictureModal, setShowFromPictureModal] = useState(false);
  const [showToPictureModal, setShowToPictureModal] = useState(false);

  // Get current project's threads
  const currentProject = projects.find(p => p.project_id === projectId);
  const threads = currentProject?.threads || [];

  // Get selected thread info
  const selectedThread = threads.find(t => t.thread_id === patternConfig.pattern_thread_id);

  // Fetch images for the selected thread
  const { images, loading: imagesLoading } = useImagesFromFirestore(
    db,
    patternConfig.pattern_project_id || undefined,
    patternConfig.pattern_thread_id || undefined,
    false
  );

  // Get selected pictures info
  const fromPicture = images.find(img => img.picture_id === patternConfig.from_picture_id);
  const toPicture = images.find(img => img.picture_id === patternConfig.to_picture_id);

  const handleThreadSelect = (thread: Thread) => {
    onPatternConfigChange({
      pattern_project_id: projectId,
      pattern_thread_id: thread.thread_id,
      from_picture_id: '',
      to_picture_id: ''
    });
    setShowThreadModal(false);
  };

  const handleFromPictureSelect = (image: IImage) => {
    onPatternConfigChange({ from_picture_id: image.picture_id });
    setShowFromPictureModal(false);
  };

  const handleToPictureSelect = (image: IImage) => {
    onPatternConfigChange({ to_picture_id: image.picture_id });
    setShowToPictureModal(false);
  };

  const canLoadPattern = patternConfig.pattern_thread_id &&
                         patternConfig.from_picture_id &&
                         patternConfig.to_picture_id &&
                         patternConfig.from_picture_id !== patternConfig.to_picture_id;

  return (
    <div className="space-y-4">
      {/* Thread Selector */}
      <div>
        <label className="text-xs text-gray-400 uppercase tracking-wide mb-2 block">
          Select Thread (from current project)
        </label>
        <button
          onClick={() => setShowThreadModal(true)}
          disabled={projectsLoading}
          className="w-full flex items-center gap-3 bg-gray-800 border border-gray-600 rounded-lg p-3 hover:border-gray-500 transition-colors text-left"
        >
          {selectedThread ? (
            <>
              <div className="w-12 h-12 bg-gray-950 rounded-lg overflow-hidden flex-shrink-0">
                {selectedThread.thumbnail_url ? (
                  <img
                    src={selectedThread.thumbnail_url}
                    alt={selectedThread.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-5 h-5 text-gray-600" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-200 truncate">{selectedThread.name}</p>
                <p className="text-xs text-gray-500">{selectedThread.length} images</p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </>
          ) : (
            <>
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                <Image className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-sm text-gray-400 flex-1">Click to select a thread...</span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </>
          )}
        </button>
      </div>

      {/* Picture Selectors - Only show if thread is selected */}
      {patternConfig.pattern_thread_id && (
        <div className="grid grid-cols-2 gap-3">
          {/* From Picture */}
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-2 block">
              Start Picture (Before)
            </label>
            <button
              onClick={() => setShowFromPictureModal(true)}
              disabled={imagesLoading}
              className="w-full flex flex-col items-center gap-2 bg-gray-800 border border-gray-600 rounded-lg p-3 hover:border-green-500/50 transition-colors"
            >
              {fromPicture ? (
                <>
                  <div className="w-full aspect-square bg-gray-950 rounded-lg overflow-hidden">
                    <img
                      src={fromPicture.output_url || fromPicture.thumb_url}
                      alt="From"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-green-400">Start Point</p>
                    <p className="text-xs text-gray-500">Step {fromPicture.seq}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-full aspect-square bg-gray-900 rounded-lg flex items-center justify-center">
                    {imagesLoading ? (
                      <Loader className="w-6 h-6 text-gray-600 animate-spin" />
                    ) : (
                      <Plus className="w-6 h-6 text-gray-600" />
                    )}
                  </div>
                  <span className="text-xs text-gray-400">Select start picture</span>
                </>
              )}
            </button>
          </div>

          {/* To Picture */}
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-2 block">
              End Picture (After)
            </label>
            <button
              onClick={() => setShowToPictureModal(true)}
              disabled={imagesLoading}
              className="w-full flex flex-col items-center gap-2 bg-gray-800 border border-gray-600 rounded-lg p-3 hover:border-purple-500/50 transition-colors"
            >
              {toPicture ? (
                <>
                  <div className="w-full aspect-square bg-gray-950 rounded-lg overflow-hidden">
                    <img
                      src={toPicture.output_url || toPicture.thumb_url}
                      alt="To"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-purple-400">End Point</p>
                    <p className="text-xs text-gray-500">Step {toPicture.seq}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-full aspect-square bg-gray-900 rounded-lg flex items-center justify-center">
                    {imagesLoading ? (
                      <Loader className="w-6 h-6 text-gray-600 animate-spin" />
                    ) : (
                      <Plus className="w-6 h-6 text-gray-600" />
                    )}
                  </div>
                  <span className="text-xs text-gray-400">Select end picture</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Pattern Info & Load Button */}
      {fromPicture && toPicture && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-blue-300">
              Pattern: Step {fromPicture.seq} &rarr; Step {toPicture.seq}
              <span className="text-blue-400 ml-1">
                ({toPicture.seq - fromPicture.seq} operation{toPicture.seq - fromPicture.seq !== 1 ? 's' : ''})
              </span>
            </div>
            <button
              onClick={onLoadPattern}
              disabled={!canLoadPattern || isLoadingPattern}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingPattern ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  Load Steps
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {patternError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded p-2 text-xs text-red-400">
          {patternError}
        </div>
      )}

      {/* Modals */}
      {showThreadModal && (
        <ThreadSelectorModal
          threads={threads}
          onSelect={handleThreadSelect}
          onClose={() => setShowThreadModal(false)}
          selectedThreadId={patternConfig.pattern_thread_id}
        />
      )}

      {showFromPictureModal && (
        <PictureSelectorModal
          images={images}
          loading={imagesLoading}
          onSelect={handleFromPictureSelect}
          onClose={() => setShowFromPictureModal(false)}
          selectedPictureId={patternConfig.from_picture_id}
          title="Select Start Picture"
        />
      )}

      {showToPictureModal && (
        <PictureSelectorModal
          images={images}
          loading={imagesLoading}
          onSelect={handleToPictureSelect}
          onClose={() => setShowToPictureModal(false)}
          selectedPictureId={patternConfig.to_picture_id}
          title="Select End Picture"
        />
      )}
    </div>
  );
};

export default PromptOverrideEditor;
